import React, { MouseEvent, useCallback, useMemo, useState } from "react";
import { Form, Divider, Menu, Icon } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { convertToRaw, ContentState } from "draft-js";
import styled from "styled-components";
import useAuthorizedContext from "hooks/useAuthorizedContext";

import FormTextInput from "../../molecules/FormFields/FormTextInput";
import FormSelect from "../../molecules/FormFields/FormSelect";
import FormDateTimeInput from "../../molecules/FormFields/FormDateTimeInput";
import useForm from "../../../hooks/useForm";
import FormMultiSelect from "../../molecules/FormFields/FormMultiSelect";
import FormGrid from "../../templates/FormGrid";
import FormRichTextArea from "../../molecules/FormFields/FormRichTextArea";
import { categoryOpts } from "../../../constants/categories";
import FormGeolocationFields from "../../molecules/FormFields/FormGeolocationInput";
import Typography, {
  definedTypography,
} from "../../atoms/Typography/Typography";
import { definedRouteTypes } from "../../../api/api";
import FormRadioButton from "../../molecules/FormFields/FormRadioButton";
import SimpleButton, {
  definedButtonTypes,
} from "../../atoms/Buttons/SimpleButton/SimpleButton";
import FormYoutubeLinkInput from "../../molecules/FormFields/FormYoutubeLinkInput";
import { IVideoNewsForm } from "../../../api";
import FormSourceField from "../../molecules/FormFields/FormSourceField";
import FormAuthorFields, {
  definedAuthorTypes,
} from "../../molecules/FormFields/FormAuthorFields";
import { IBaseFormProps } from "../../../interfaces/forms";
import FormKeywordSelect from "../../molecules/FormFields/FormKeywordSelect";
import useLocalizedCountryOpts from "../../../hooks/useLocalizedCountryOpts";
import FormRankSelect from "../../molecules/FormFields/FormRankSelect";
import useSubmitRankedPosts from "../../../hooks/useSubmitRankedPosts";
import FormVideoLinkInput from "../../molecules/FormFields/FormVideoLinkInput";
import { definedTypographyTypes } from "../../atoms/Typography/TypographyStyles";
import { isYoutubeLink } from "../../../helpers/regexp";
import FormRegionInput from "../../molecules/FormFields/FormRegionInput";

interface VideoNewsFormProps extends IBaseFormProps {
  update?: boolean;
  initialValues?: IVideoNewsForm;
  onFormClose?: (event?: MouseEvent) => void;
  type?: definedRouteTypes.videoNews;
}

const StyledDiv = styled.div({
  textAlign: "center",
});
const StyledIcon = styled(Icon)({
  "&&&": {
    marginLeft: "0.5em !important",
  },
});
enum definedMediaLink {
  youtube = "youtube",
  iframe = "iframe",
}

const VideoNewsForm: React.FC<VideoNewsFormProps> = ({
  initialValues,
  onFormClose,
  update,
}) => {
  const CountryOptions = useLocalizedCountryOpts();
  const [isSubmitted, setSubmitted] = useState(update);
  const { user } = useAuthorizedContext();
  const defaultInitValues = useCallback((): IVideoNewsForm => {
    return {
      title: "",
      slug: "",
      description: convertToRaw(ContentState.createFromText("")),
      imagePointer: undefined,
      mediaLink: "",
      category: undefined,
      authorType: definedAuthorTypes.none,
      author: user.email,
      creator: user.username,
      creatorName: user.displayName(),
      organizationId: "",
      organizationInfo: undefined,
      subCategories: [],
      highlighted: false,
      show: true,
      countries: undefined,
      address: undefined,
      source: { name: "", url: "" },
      type: definedRouteTypes.videoNews,
      displayInHomePage: false,
      online: false,
      region: undefined,
    };
  }, [user]);
  const { t } = useTranslation();

  const submitFormQuery = useSubmitRankedPosts<IVideoNewsForm>(isSubmitted);

  const defaultInitialValues = useMemo(() => defaultInitValues(), [
    defaultInitValues,
  ]);

  const {
    values,
    submitForm,
    changeFormValue,
    isLoading,
    changeFormImageValue,
  } = useForm<IVideoNewsForm>(
    initialValues || defaultInitialValues,
    submitFormQuery,
  );

  const {
    title,
    description,
    category,
    subCategories,
    address,
    source,
    show,
    exclusive,
    highlighted,
    toBePostedAt,
    countries,
    authorType,
    author,
    organizationId,
    anotherAuthor,
    imageAuthor,
    imageUrl,
    customLabel,
    mediaLink,
    displayInHomePage,
    online,
    homepageRank,
    region,
  } = values;

  const [mediaType, setMediaType] = useState(
    isYoutubeLink(mediaLink)
      ? definedMediaLink.youtube
      : definedMediaLink.iframe,
  );
  const changeMediaTab = (type: definedMediaLink) => {
    changeFormValue("mediaLink", "");
    setMediaType(type);
  };
  const formTitle = isSubmitted
    ? t("Forms.Titles.EditHeader")
    : t("Forms.Titles.AddHeader");
  const submit = isSubmitted
    ? t("Forms.Actions.Update")
    : t("Forms.Actions.Create");
  const submitAndClose = isSubmitted
    ? t("Forms.Actions.UpdateAndClose")
    : t("Forms.Actions.CreateAndClose");

  const convertedShow = Boolean(Number(show));
  const convertedDisplay = Boolean(Number(displayInHomePage));

  return (
    <Form name="editorialNewsForm" loading={isLoading}>
      <Typography type={definedTypography.h1} as="span">
        {formTitle}
      </Typography>
      <Divider />
      <FormGrid label={t("Administrēšana")}>
        <FormRadioButton
          id="show"
          label="Redzams portālā"
          value={convertedShow}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="displayInHomePage"
          label="Rādīt sākumlapā"
          value={convertedDisplay}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="exclusive"
          label="Tikai Latvieši.com"
          value={exclusive}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="highlighted"
          label="Izcelts"
          value={highlighted}
          onChange={changeFormValue}
        />
        <FormRankSelect
          label={t("Rādīt sākumlapā")}
          id="homepageRank"
          value={homepageRank}
          type={definedRouteTypes.videoNews}
          required
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Forms.Titles.Content")}>
        <FormTextInput
          id="title"
          required
          value={title}
          label={t("Forms.Fields.title")}
          onChange={changeFormValue}
        />
        <Menu tabular>
          <Menu.Item
            id="youtube"
            active={mediaType === definedMediaLink.youtube}
            onClick={() => changeMediaTab(definedMediaLink.youtube)}
          >
            <Typography type={definedTypographyTypes.caption}>
              {t("Forms.Tabs.Youtube")}
            </Typography>
            <StyledIcon name="youtube" />
          </Menu.Item>
          <Menu.Item
            id="iframe"
            active={mediaType === definedMediaLink.iframe}
            onClick={() => changeMediaTab(definedMediaLink.iframe)}
          >
            <Typography type={definedTypographyTypes.caption}>
              {t("Forms.Tabs.Other")}
            </Typography>
            <StyledIcon name="linkify" />
          </Menu.Item>
        </Menu>
        {mediaType === definedMediaLink.youtube ? (
          <FormYoutubeLinkInput
            id="mediaLink"
            required
            label={t("Forms.Fields.mediaLinkYT")}
            value={mediaLink}
            onChange={changeFormValue}
          />
        ) : (
          <FormVideoLinkInput
            id="mediaLink"
            required
            label={t("Forms.Fields.mediaLink")}
            value={mediaLink}
            changeFormImageValue={changeFormImageValue}
            onChange={changeFormValue}
            imageUrl={imageUrl}
          />
        )}
        <FormRichTextArea
          id="description"
          required
          label={t("Forms.Fields.description")}
          value={description}
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Forms.Titles.Author")}>
        <FormAuthorFields
          authorType={authorType}
          author={author}
          organizationId={organizationId}
          anotherAuthor={anotherAuthor}
          changeFormValue={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Forms.Titles.Category")}>
        <FormSelect
          label={t("Forms.Fields.category")}
          id="category"
          value={category}
          options={categoryOpts}
          onChange={changeFormValue}
          search
        />
        <FormKeywordSelect
          label={t("Forms.Fields.subCategories")}
          id="subCategories"
          value={subCategories || []}
          onChange={changeFormValue}
          selectedCategory={category}
        />
        <FormMultiSelect
          id="countries"
          label={t("Forms.Fields.countries")}
          options={CountryOptions}
          value={countries || []}
          onChange={changeFormValue}
          search
        />
        <FormRegionInput
          id="region"
          value={region}
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Forms.Titles.Place")}>
        <FormRadioButton
          id="online"
          label={t("Forms.Fields.Online")}
          value={online}
          onChange={changeFormValue}
        />
        <FormGeolocationFields
          id="address"
          value={address}
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Forms.Titles.Settings")}>
        <FormDateTimeInput
          id="toBePostedAt"
          timeLabel={t("Forms.Fields.toBePostedAtTime")}
          dateLabel={t("Forms.Fields.toBePostedAtDate")}
          value={toBePostedAt}
          onChange={changeFormValue}
        />
        <FormSourceField
          id="source"
          value={source}
          onChange={changeFormValue}
        />
        <FormTextInput
          id="imageAuthor"
          label={t("Forms.Fields.imageAuthor")}
          value={imageAuthor}
          onChange={changeFormValue}
        />
        <FormTextInput
          id="customLabel"
          label={t("Forms.Fields.customLabel")}
          value={customLabel}
          onChange={changeFormValue}
        />
      </FormGrid>
      <StyledDiv>
        {onFormClose && (
          <SimpleButton
            id="FormCancel"
            variant={definedButtonTypes.secondary}
            onClick={onFormClose}
          >
            {t("Forms.Actions.Cancel")}
          </SimpleButton>
        )}
        <SimpleButton
          id="FormSubmit"
          onClick={async (event) => {
            // TODO this is quickfix to enable updating...
            await submitForm(event, (res) => {
              setSubmitted(true);
              changeFormValue("slug", res ? res.slug : "");
            });
          }}
          variant={definedButtonTypes.secondary}
        >
          {submit}
        </SimpleButton>
        <SimpleButton
          id="FormSubmitAndClose"
          onClick={async (event) => {
            await submitForm(event, () => {
              if (onFormClose) onFormClose();
            });
          }}
          variant={definedButtonTypes.primary}
        >
          {submitAndClose}
        </SimpleButton>
      </StyledDiv>
    </Form>
  );
};

VideoNewsForm.defaultProps = {
  type: definedRouteTypes.videoNews,
};

export default VideoNewsForm;
